import { FlexCol } from '@/components/common/Flex';
import { transparentize } from '@/util/chakra';
import {
    Divider,
    FlexProps,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
} from '@chakra-ui/react';
import { toPx } from '@common/util/styles';
import { useState } from 'react';

const NONLINEAR_POWER = 0.25; // Closer to 1 is more linear, further is more nonlinear

interface WithSliderInputProps extends Omit<FlexProps, 'onChange'> {
    min: number;
    max: number;
    step: number;
    value: number;
    scaleWhenActive?: number;
    nonlinear?: boolean;
    onChange: (value: number) => void;
}

const WithSliderInput = ({
    min,
    max,
    step,
    value,
    scaleWhenActive: scale = 1,
    nonlinear,
    onChange,
    ...props
}: WithSliderInputProps) => {
    const [isActive, setIsActive] = useState(false);

    const offset = isActive && scale > 1 ? 1.5 : -0.5;

    const transform = isActive && scale > 1 ? `scale(${scale})` : undefined;

    // When active, counteract Slider transform (/ scale) and increase (+ 0.5)
    const thumbSize = isActive && scale > 1 ? 3.5 / scale + 0.5 : 3.5;

    const linearToAdjustedPosition = (value: number) => {
        return 100 * Math.pow(value / max, NONLINEAR_POWER);
    };

    const adjustedPositionToLinear = (value: number) => {
        const linear = max * Math.pow(value / 100, 1 / NONLINEAR_POWER);
        return Math.round(linear / 1000) * 1000;
    };

    return (
        <FlexCol transition="all 0.2s" mt={offset} {...props}>
            <Divider
                height="1px"
                borderColor="gray.300"
                width="full"
                transform={transform}
                opacity={1}
            />
            <Slider
                min={nonlinear ? 1 : min}
                max={nonlinear ? 100 : max}
                step={nonlinear ? 1 : step}
                value={nonlinear ? linearToAdjustedPosition(value) : value}
                onChange={(value) =>
                    onChange(
                        nonlinear
                            ? Math.round(adjustedPositionToLinear(value))
                            : value
                    )
                }
                focusThumbOnChange={false}
                onChangeStart={() => {
                    setIsActive(true);
                }}
                onChangeEnd={() => {
                    setIsActive(false);
                }}
                mt={toPx(-7)}
                transform={transform}
            >
                <SliderTrack bg="transparent">
                    <SliderFilledTrack bg="blue.500" />
                </SliderTrack>
                <SliderThumb
                    backgroundColor="blue.500"
                    width={thumbSize}
                    height={thumbSize}
                    _before={{
                        content: '""',
                        display: 'block',
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: transparentize('blue.500', 0.2),
                        position: 'absolute',
                        top: '-8px',
                        left: '-8px',
                        opacity: 0,
                        transition: 'opacity 0.2s ease-in-out',
                    }}
                    _hover={{
                        '::before': {
                            opacity: 1,
                        },
                    }}
                ></SliderThumb>
            </Slider>
        </FlexCol>
    );
};

export default WithSliderInput;
